import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  IconButton,
  Box,
  FormControlLabel,
  Switch,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import { type OperatorRecord } from './OperatorImporterView';
import {
  OPERATOR_SKILL_OPTIONS,
  OPERATOR_EXPERIENCE_OPTIONS,
  operatorSkillLabelKey,
} from './operatorImporterUtils';

interface FormData {
  firstName: string;
  lastName: string;
  role: string;
  assetId: string;
  skills: string[];
  experience: string;
  isActive: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
}

interface AssetOption {
  id: string;
  bumperNumber: string;
}

interface Props {
  open: boolean;
  operator: OperatorRecord | null;
  onClose: () => void;
  onSave: (
    updated: Omit<
      OperatorRecord,
      'id' | 'pin' | 'assetVin' | 'assetBumperNumber'
    >
  ) => Promise<void>;
  assetOptions: AssetOption[];
}

export const EditOperatorModal = ({
  open,
  operator,
  onClose,
  onSave,
  assetOptions,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormData>({
    firstName: '',
    lastName: '',
    role: '',
    assetId: '',
    skills: [],
    experience: '',
    isActive: true,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (operator) {
      setForm({
        firstName: operator.firstName,
        lastName: operator.lastName,
        role: operator.role,
        assetId: operator.assetId ?? '',
        skills: operator.skills ?? [],
        experience: operator.experience ?? '',
        isActive: operator.isActive,
      });
      setErrors({});
    }
  }, [operator]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.firstName.trim())
      next.firstName = t('operator_importer_err_required');
    if (!form.lastName.trim())
      next.lastName = t('operator_importer_err_required');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({
        operatorName: `${form.firstName.trim()} ${form.lastName.trim()}`,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        role: form.role.trim(),
        isActive: form.isActive,
        assetId: form.assetId.trim() !== '' ? form.assetId.trim() : null,
        skills: form.skills,
        experience: form.experience !== '' ? form.experience : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  const field =
    (key: keyof Pick<FormData, 'firstName' | 'lastName'>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.primary.main, 0.25),
            }}
          >
            <PersonOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('operator_importer_edit_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {operator?.operatorName}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box
        sx={{
          mx: 3,
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: (t) => alpha(t.palette.divider, 0.6),
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
        >
          {t('operator_importer_section_info')}
        </Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('operator_importer_field_first_name')}
              fullWidth
              size="small"
              value={form.firstName}
              onChange={field('firstName')}
              error={!!errors.firstName}
              helperText={errors.firstName}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('operator_importer_field_last_name')}
              fullWidth
              size="small"
              value={form.lastName}
              onChange={field('lastName')}
              error={!!errors.lastName}
              helperText={errors.lastName}
              inputProps={{ maxLength: 100 }}
            />
          </Grid>

          {/* Role */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('operator_importer_field_role')}</InputLabel>
              <Select
                label={t('operator_importer_field_role')}
                value={form.role}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, role: e.target.value }));
                }}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                <MenuItem value="driver">
                  {t('operator_importer_role_driver')}
                </MenuItem>
                <MenuItem value="root">
                  {t('operator_importer_role_root')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Experience */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('operator_importer_field_experience')}</InputLabel>
              <Select
                label={t('operator_importer_field_experience')}
                value={form.experience}
                onChange={(e) => {
                  setForm((prev) => ({ ...prev, experience: e.target.value }));
                }}
              >
                <MenuItem value="">
                  <em>—</em>
                </MenuItem>
                {OPERATOR_EXPERIENCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {t(`operator_experience_${opt.toLowerCase()}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Skills */}
          <Grid item xs={12}>
            <Autocomplete
              multiple
              options={[...OPERATOR_SKILL_OPTIONS]}
              isOptionEqualToValue={(option, value) => option === value}
              getOptionLabel={(opt) =>
                t(operatorSkillLabelKey(opt), { defaultValue: opt })
              }
              value={form.skills}
              onChange={(_, val) => {
                setForm((prev) => ({ ...prev, skills: val }));
              }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('operator_importer_field_skills')}
                />
              )}
            />
          </Grid>

          {/* Asset */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={assetOptions}
              getOptionLabel={(o) => o.bumperNumber}
              value={assetOptions.find((a) => a.id === form.assetId) ?? null}
              onChange={(_, val) => {
                setForm((prev) => ({ ...prev, assetId: val?.id ?? '' }));
              }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('operator_importer_col_asset_bumper')}
                />
              )}
            />
          </Grid>

          {/* Active toggle */}
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      isActive: e.target.checked,
                    }));
                  }}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {t('operator_importer_field_active')}
                </Typography>
              }
            />
          </Grid>
        </Grid>

        {operator?.pin && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
              border: '1px solid',
              borderColor: (t) => alpha(t.palette.divider, 0.5),
              display: 'inline-flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {t('operator_importer_col_pin')}:
            </Typography>
            <Typography
              variant="caption"
              fontWeight={700}
              fontFamily="monospace"
            >
              {operator.pin}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (t) => alpha(t.palette.divider, 0.5),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleClose}
          disabled={saving}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('operator_importer_cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            void handleSubmit();
          }}
          disabled={saving}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : undefined
          }
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('operator_importer_save_changes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
